import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Anchor as AnchorIcon,
  Delete as DeleteIcon,
  FlightTakeoff as FlightTakeoffIcon,
  Map as MapIcon,
  Place as PlaceIcon,
  Refresh,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { companyLocationApi } from '../services/api';
import {
  CompanyLocation,
  CompanyLocationUpsertRequest,
  GeocodeResult,
  formatCompanyLocationLabel,
} from '../types/companyLocation';
import { useDomainSettingsQuery } from '../settings/useDomainSettingsQuery';
import { LocationMapInlineEditor } from '../components/locations/LocationMapInlineEditor';
import { DETAIL_FORM_UNIFORM_FIELDS_SX } from '../styles/detailFormStyles';

const emptyFormData: CompanyLocationUpsertRequest = {
  name: '',
  address: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  latitude: null,
  longitude: null,
  plusCode: null,
  active: true,
  servesAirport: false,
  servesPort: false,
};

const buildEmptyFormData = (): CompanyLocationUpsertRequest => ({
  ...emptyFormData,
});

const mapLocationToFormData = (location: CompanyLocation): CompanyLocationUpsertRequest => ({
  name: location.name || '',
  address: location.address || '',
  city: location.city || '',
  state: location.state || '',
  postalCode: location.postalCode || '',
  country: location.country || '',
  latitude: location.latitude ?? null,
  longitude: location.longitude ?? null,
  plusCode: location.plusCode ?? null,
  active: location.active ?? true,
  servesAirport: location.servesAirport ?? false,
  servesPort: location.servesPort ?? false,
});

const parseOptionalNumber = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

// Kept short on purpose: street + city is enough to identify a location, and state/
// postal code/country just make this summary unwieldy (Albania barely uses either).
const buildLocationAddressSummary = (location?: Partial<CompanyLocation> | null) =>
  [location?.address, location?.city]
    .filter(Boolean)
    .join(', ');

const formatCoordinateValue = (value?: number | null) =>
  typeof value === 'number' ? value.toFixed(6) : '-';

export const CompanyLocationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const companyId = user?.company?.id;
  const [selectedLocation, setSelectedLocation] = useState<CompanyLocation | null>(null);
  const [formData, setFormData] = useState<CompanyLocationUpsertRequest>(buildEmptyFormData);
  const [isCreating, setIsCreating] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const settingsQuery = useDomainSettingsQuery(companyId);
  const multipleLocationsEnabled = Boolean(settingsQuery.data?.multipleLocations);

  const {
    data: locations = [],
    isLoading,
    refetch,
  } = useQuery(['companyLocations', companyId], () => companyLocationApi.getAll(false), {
    enabled: !!companyId,
  });

  const loadLocationIntoEditor = (location: CompanyLocation) => {
    setSelectedLocation(location);
    setFormData(mapLocationToFormData(location));
    setIsCreating(false);
    setIsDirty(false);
  };

  const handleStartCreate = () => {
    setSelectedLocation(null);
    setFormData(buildEmptyFormData());
    setIsCreating(true);
    setIsDirty(false);
  };

  const resetEditor = () => {
    setSelectedLocation(null);
    setFormData(buildEmptyFormData());
    setIsCreating(false);
    setIsDirty(false);
  };

  const updateFormValue = <K extends keyof CompanyLocationUpsertRequest>(
    field: K,
    value: CompanyLocationUpsertRequest[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);
  };

  const createMutation = useMutation((data: CompanyLocationUpsertRequest) => companyLocationApi.create(data), {
    onSuccess: (createdLocation) => {
      queryClient.invalidateQueries(['companyLocations', companyId]);
      toast.success(
        t('locations.messages.created', { defaultValue: 'Location created successfully' })
      );
      loadLocationIntoEditor(createdLocation);
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error ||
          t('locations.messages.createFailed', { defaultValue: 'Failed to create location' })
      );
    },
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: CompanyLocationUpsertRequest }) =>
      companyLocationApi.update(id, data),
    {
      onSuccess: (updatedLocation) => {
        queryClient.invalidateQueries(['companyLocations', companyId]);
        toast.success(
          t('locations.messages.updated', { defaultValue: 'Location updated successfully' })
        );
        loadLocationIntoEditor(updatedLocation);
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error ||
            t('locations.messages.updateFailed', { defaultValue: 'Failed to update location' })
        );
      },
    }
  );

  const deleteMutation = useMutation((id: string) => companyLocationApi.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(['companyLocations', companyId]);
      toast.success(
        t('locations.messages.deleted', { defaultValue: 'Location deleted successfully' })
      );
      setOpenDeleteDialog(false);
      resetEditor();
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error ||
          t('locations.messages.deleteFailed', { defaultValue: 'Failed to delete location' })
      );
    },
  });

  const handleSave = () => {
    if (!formData.name.trim() || !formData.address.trim() || !formData.city.trim() || !formData.country.trim()) {
      toast.error(
        t('locations.validation.required', {
          defaultValue: 'Please fill in the required location fields.',
        })
      );
      return;
    }

    if (isCreating) {
      createMutation.mutate(formData);
      return;
    }

    if (selectedLocation) {
      updateMutation.mutate({ id: selectedLocation.id.toString(), data: formData });
    }
  };

  const handleMapPickerConfirm = (result: GeocodeResult) => {
    setFormData((prev) => ({
      ...prev,
      latitude: result.latitude,
      longitude: result.longitude,
      plusCode: result.plusCode || prev.plusCode,
      address: result.address || prev.address,
      city: result.city || prev.city,
      state: result.state ?? prev.state,
      postalCode: result.postalCode ?? prev.postalCode,
      country: result.country || prev.country,
    }));
    setIsDirty(true);
  };

  if (settingsQuery.isLoading || isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const isSaving = createMutation.isLoading || updateMutation.isLoading;
  const hasSelection = isCreating || Boolean(selectedLocation);
  const selectedIsPrimary = Boolean(selectedLocation?.primaryCompanyAddress);
  const activeLocationsCount = locations.filter((location) => location.active).length;
  const locationHeading = isCreating
    ? t('locations.newTitle', { defaultValue: 'New Location' })
    : selectedLocation?.name || t('locations.detailsTitle', { defaultValue: 'Location Details' });
  const addressSummary =
    buildLocationAddressSummary(formData) ||
    buildLocationAddressSummary(selectedLocation) ||
    formatCompanyLocationLabel(selectedLocation) ||
    t('locations.summaryFallback', {
      defaultValue: 'Add the address and pickup details to complete this point.',
    });

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 1100,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          width: '100%',
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar
            sx={{
              pl: { xs: 2, sm: 3 },
              pr: { xs: 2, sm: 3 },
              minHeight: '48px !important',
              py: 0.5,
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleStartCreate}
                size="small"
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                }}
              >
                {t('common.new')}
              </Button>
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={!hasSelection || !isDirty || isSaving}
                size="small"
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                }}
              >
                {t('common.save')}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setOpenDeleteDialog(true)}
                disabled={!selectedLocation || selectedIsPrimary || deleteMutation.isLoading}
                size="small"
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                }}
              >
                {t('common.delete')}
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </Box>

      <Container maxWidth="xl" disableGutters sx={{ py: 1 }}>
        {!multipleLocationsEnabled && (
          <Alert severity="info" sx={{ mb: 1.5, mx: { xs: 2, sm: 3 } }}>
            {t('locations.disabledInfo', {
              defaultValue:
                'Multiple locations are currently disabled. You can prepare point data here, and RentMan will start using it once the company setting is enabled.',
            })}
          </Alert>
        )}

        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 0 }}>
              <Toolbar
                sx={{
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pl: { xs: 2, sm: 3 },
                  pr: { xs: 2, sm: 3 },
                  minHeight: '48px !important',
                  py: 0.5,
                  gap: 1,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <PlaceIcon color="primary" sx={{ fontSize: 20 }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ lineHeight: 1.1 }}>
                      {t('locations.title', { defaultValue: 'Locations' })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('locations.tableSubtitle', {
                        defaultValue: 'Manage the points where you do pickup and return.',
                      })}
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ flexGrow: 1 }} />

                <Button
                  variant="outlined"
                  startIcon={<Refresh fontSize="small" />}
                  onClick={() => refetch()}
                  size="small"
                  sx={{
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5,
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    '& .MuiButton-startIcon': {
                      marginRight: 0.5,
                    },
                  }}
                >
                  {t('common.refresh')}
                </Button>

                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={t('locations.totalCount', {
                      defaultValue: `Points: ${locations.length}`,
                      count: locations.length,
                    })}
                  />
                  <Chip
                    size="small"
                    label={t('locations.activeCountLabel', {
                      count: activeLocationsCount,
                      defaultValue: `Active: ${activeLocationsCount}`,
                    })}
                  />
                </Stack>
              </Toolbar>

              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    tableLayout: 'fixed',
                    width: '100%',
                    '& .MuiTableCell-root': {
                      fontSize: '0.875rem',
                      px: { xs: 2, sm: 3 },
                    },
                  }}
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        '& .MuiTableCell-root': {
                          borderBottomColor: 'divider',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        },
                      }}
                    >
                      <TableCell sx={{ width: '28%' }}>{t('fields.name')}</TableCell>
                      <TableCell sx={{ width: '42%' }}>{t('fields.location')}</TableCell>
                      <TableCell sx={{ width: '18%' }}>{t('fields.city')}</TableCell>
                      <TableCell sx={{ width: '12%' }}>{t('fields.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {locations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          {t('locations.empty', { defaultValue: 'No company locations configured yet.' })}
                        </TableCell>
                      </TableRow>
                    ) : (
                      locations.map((location) => (
                        <TableRow
                          key={location.id}
                          hover
                          selected={!isCreating && selectedLocation?.id === location.id}
                          sx={{ cursor: 'pointer' }}
                          onClick={() => loadLocationIntoEditor(location)}
                        >
                          <TableCell sx={{ py: 1 }}>
                            <Stack spacing={0.5}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                {location.name}
                              </Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {location.primaryCompanyAddress && (
                                  <Chip
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    label={t('locations.primaryCompanyAddress', {
                                      defaultValue: 'Company address',
                                    })}
                                  />
                                )}
                                {location.servesAirport && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                    icon={<FlightTakeoffIcon sx={{ fontSize: 14 }} />}
                                    label={t('locations.servesAirport', {
                                      defaultValue: 'Airport',
                                    })}
                                  />
                                )}
                                {location.servesPort && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                    icon={<AnchorIcon sx={{ fontSize: 14 }} />}
                                    label={t('locations.servesPort', {
                                      defaultValue: 'Port',
                                    })}
                                  />
                                )}
                              </Stack>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              title={buildLocationAddressSummary(location)}
                            >
                              {location.address}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <Typography variant="body2" noWrap>
                              {[location.city, location.country].filter(Boolean).join(', ')}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <Chip
                              size="small"
                              label={location.active ? t('status.active') : t('status.inactive')}
                              color={location.active ? 'success' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {hasSelection ? (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ mt: 2, p: 1, display: 'flex', flexDirection: 'column', ...DETAIL_FORM_UNIFORM_FIELDS_SX }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h6">{locationHeading}</Typography>
                  {formData.active && (
                    <Chip
                      size="small"
                      color="success"
                      label={t('status.active')}
                    />
                  )}
                  {selectedLocation?.primaryCompanyAddress && !isCreating && (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={t('locations.primaryCompanyAddress', {
                        defaultValue: 'Company address',
                      })}
                    />
                  )}
                  {formData.servesAirport && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="info"
                      icon={<FlightTakeoffIcon sx={{ fontSize: 14 }} />}
                      label={t('locations.servesAirport', { defaultValue: 'Airport' })}
                    />
                  )}
                  {formData.servesPort && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="info"
                      icon={<AnchorIcon sx={{ fontSize: 14 }} />}
                      label={t('locations.servesPort', { defaultValue: 'Port' })}
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {addressSummary}
                </Typography>
                <Divider sx={{ mb: 1 }} />

                {selectedLocation?.primaryCompanyAddress && !isCreating && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    {t('locations.primaryLocationHint', {
                      defaultValue:
                        'This row is the company address location. If you change the main company address in settings, this location stays in sync and cannot be deleted.',
                    })}
                  </Alert>
                )}

                <Grid container spacing={1}>
                  <Grid item xs={12} md={5}>
                    <Stack spacing={1}>
                      <Paper variant="outlined" sx={{ p: 1.25 }}>
                        <Stack spacing={1}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {t('locations.generalTitle', { defaultValue: 'General' })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('locations.generalSubtitle', {
                                defaultValue: 'Keep the point name short and consistent.',
                              })}
                            </Typography>
                          </Box>

                          <TextField
                            fullWidth
                            size="small"
                            required
                            label={t('fields.name')}
                            value={formData.name}
                            onChange={(event) => updateFormValue('name', event.target.value)}
                          />
                        </Stack>
                      </Paper>

                      <Paper variant="outlined" sx={{ p: 1.25 }}>
                        <Stack spacing={1}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {t('locations.addressTitle', {
                                defaultValue: 'Address',
                              })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('locations.addressSubtitle', {
                                defaultValue: 'Use the exact street information for this pickup/return point.',
                              })}
                            </Typography>
                          </Box>

                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                required
                                label={t('fields.streetAddress')}
                                value={formData.address}
                                onChange={(event) => updateFormValue('address', event.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                size="small"
                                required
                                label={t('fields.city')}
                                value={formData.city}
                                onChange={(event) => updateFormValue('city', event.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label={t('fields.state')}
                                value={formData.state || ''}
                                onChange={(event) => updateFormValue('state', event.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label={t('fields.postalCode')}
                                value={formData.postalCode || ''}
                                onChange={(event) => updateFormValue('postalCode', event.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                size="small"
                                required
                                label={t('fields.country')}
                                value={formData.country}
                                onChange={(event) => updateFormValue('country', event.target.value)}
                              />
                            </Grid>
                          </Grid>
                        </Stack>
                      </Paper>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <Stack spacing={1}>
                      <Paper variant="outlined" sx={{ p: 1.25 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <MapIcon color="primary" sx={{ fontSize: 18 }} />
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {t('locations.mapSectionTitle', { defaultValue: 'Pickup point map' })}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('locations.mapSectionSubtitle', {
                                  defaultValue: 'Pick the point on the map, resolve the address, and apply it to the form.',
                                })}
                              </Typography>
                            </Box>
                          </Stack>

                          <LocationMapInlineEditor
                            latitude={formData.latitude}
                            longitude={formData.longitude}
                            apiKey={settingsQuery.data?.googleMapsApiKey}
                            onApply={handleMapPickerConfirm}
                          />

                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label={t('fields.latitude', { defaultValue: 'Latitude' })}
                                value={formData.latitude ?? ''}
                                onChange={(event) =>
                                  updateFormValue('latitude', parseOptionalNumber(event.target.value))
                                }
                                helperText={formatCoordinateValue(formData.latitude)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label={t('fields.longitude', { defaultValue: 'Longitude' })}
                                value={formData.longitude ?? ''}
                                onChange={(event) =>
                                  updateFormValue('longitude', parseOptionalNumber(event.target.value))
                                }
                                helperText={formatCoordinateValue(formData.longitude)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label={t('locations.plusCode', { defaultValue: 'Plus Code' })}
                                value={formData.plusCode || ''}
                                InputProps={{ readOnly: true }}
                              />
                            </Grid>
                          </Grid>
                        </Stack>
                      </Paper>

                      <Paper variant="outlined" sx={{ p: 1.25 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                          {t('locations.operationalTitle', { defaultValue: 'Operational status' })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                          {t('locations.operationalSubtitle', {
                            defaultValue: 'Choose whether this point is active and available for pickup and return.',
                          })}
                        </Typography>
                        <Stack spacing={0}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={formData.active}
                                onChange={(event) => updateFormValue('active', event.target.checked)}
                              />
                            }
                            label={t('status.active')}
                          />
                        </Stack>
                      </Paper>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ mt: 2, p: 3 }}>
                <Stack spacing={1.5} alignItems="center" textAlign="center">
                  <PlaceIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
                  <Typography variant="h6">
                    {t('locations.editorEmptyTitle', {
                      defaultValue: 'Select a location to edit',
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('locations.editorEmptyBody', {
                      defaultValue:
                        'Choose a point from the table or create a new one to manage its address and availability rules.',
                    })}
                  </Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={handleStartCreate}>
                    {t('common.new')}
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          )}
        </Grid>

        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
          <DialogTitle>{t('locations.deleteTitle', { defaultValue: 'Delete location?' })}</DialogTitle>
          <DialogContent>
            <Typography>
              {selectedIsPrimary
                ? t('locations.primaryDeleteBlocked', {
                    defaultValue:
                      'The company address location cannot be deleted. Update the company profile address instead.',
                  })
                : t('locations.deleteBody', {
                    defaultValue: 'This location will be removed if it is not assigned anywhere.',
                  })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>{t('common.cancel')}</Button>
            <Button
              variant="contained"
              color="error"
              disabled={!selectedLocation || selectedIsPrimary || deleteMutation.isLoading}
              onClick={() => selectedLocation && deleteMutation.mutate(selectedLocation.id.toString())}
            >
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};
